import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientDossiers } from '../../hooks/queries/useClientDossiers';
import { DOSSIER_STAGE_LABELS } from '../../services/dossiers/dossierService';
import type { DossierStage } from '../../services/dossiers/dossierService';

type Filter = 'open' | 'closed';

export function PortalDossiersListPage() {
  const navigate = useNavigate();
  const { data: dossiers = [], isLoading } = useClientDossiers();
  const [filter, setFilter] = useState<Filter>('open');

  const filtered = useMemo(
    () =>
      dossiers.filter((d) =>
        filter === 'open' ? !d.closed_at : !!d.closed_at,
      ),
    [dossiers, filter],
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1
          className="text-xl font-semibold text-cime-text-primary"
          style={{ fontFamily: 'var(--cime-font-heading)' }}
        >
          Mes dossiers
        </h1>
        <div className="inline-flex rounded-md border border-neutral-200 p-0.5">
          {(['open', 'closed'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded text-xs font-medium ${
                filter === f
                  ? 'bg-cime-primary text-white'
                  : 'text-cime-text-muted hover:bg-neutral-50'
              }`}
            >
              {f === 'open' ? 'En cours' : 'Clôturés'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-cime-text-muted">Chargement…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-cime-text-muted">
          Aucun dossier {filter === 'open' ? 'en cours' : 'clôturé'}.
        </p>
      ) : (
        <div className="bg-cime-surface border border-neutral-200 rounded-cime overflow-hidden">
          <table className="cime-table text-sm">
            <thead>
              <tr>
                <th className="text-left px-3 py-2.5">Numéro</th>
                <th className="text-left px-3 py-2.5">Intitulé</th>
                <th className="text-left px-3 py-2.5">Étape</th>
                <th className="text-left px-3 py-2.5">Avocat</th>
                <th className="text-left px-3 py-2.5">Ouvert le</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => navigate(`/portail/dossiers/${d.id}`)}
                  className="cursor-pointer"
                >
                  <td className="px-3 py-2.5 font-medium">{d.number}</td>
                  <td className="px-3 py-2.5">{d.title}</td>
                  <td className="px-3 py-2.5">
                    {d.stage
                      ? DOSSIER_STAGE_LABELS[d.stage as DossierStage]
                      : '—'}
                  </td>
                  <td className="px-3 py-2.5">{d.lead_counsel_name ?? '—'}</td>
                  <td className="px-3 py-2.5">
                    {d.opened_at
                      ? new Date(d.opened_at).toLocaleDateString('fr-FR')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
